/**
 * Deteccion de llegada a un punto.
 *
 * Regla de negocio 1: al entrar en el radio se avisa al servidor, y solo si el
 * servidor responde que es la primera visita se dispara el video. El estado
 * local nunca decide, porque watchPosition rebota: entra y sale del radio
 * varias veces seguidas mientras el GPS se estabiliza.
 *
 * Aqui se anaden tres amortiguadores por encima de eso:
 *  - un candado por punto, para no lanzar dos peticiones a la vez del mismo
 *    punto mientras la primera esta en vuelo,
 *  - un descarte de lecturas muy imprecisas, que en ciudad llegan a decenas de
 *    metros y provocarian entradas fantasma en puntos por los que no se ha
 *    pasado,
 *  - y la eleccion de UN punto cuando hay varios en el radio.
 *
 * Lo ultimo se decide en `clasificarLlegada`, del paquete compartido, que es
 * donde se puede probar sin navegador. Hasta el 11/09/2026 este hook registraba
 * visita en TODOS los puntos del radio y abria la ficha del que contestara
 * antes el servidor: no el mas cercano, el que ganara la carrera.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { clasificarLlegada, type PuntoPublico } from '@recorregut/shared';
import type { PosicionActual } from './useGeolocalizacion.ts';
import { useRegistrarVisita } from '@/api/consultas.ts';

/**
 * Por encima de esta imprecision la lectura no se usa para activar puntos. El
 * valor sale del margen que la propia especificacion reconoce en entornos
 * urbanos, donde el GPS llega a errar entre 30 y 50 metros.
 */
const PRECISION_MAXIMA_ACEPTABLE = 100;

interface Aviso {
  punto: PuntoPublico;
  primeraVez: boolean;
  preguntaDisponible: boolean;
}

interface Resultado {
  /** Punto en el que se acaba de entrar, para que la interfaz reaccione. */
  llegada: Aviso | null;
  descartarLlegada: () => void;
  /** Ids de puntos dentro del radio ahora mismo. */
  dentroDeRadio: Set<number>;
  distancias: Map<number, number>;
  /**
   * Puntos igual de cerca entre los que hay que elegir. Vacio casi siempre:
   * solo se llena cuando dos estan a menos de diez metros de diferencia, que es
   * cuando "el mas cercano" deja de significar nada frente al error del GPS.
   */
  candidatos: PuntoPublico[];
  /** La persona dice en cual esta. */
  elegirPunto: (punto: PuntoPublico) => void;
}

export function useProximidad(
  puntos: PuntoPublico[],
  posicion: PosicionActual | null,
  activo: boolean,
): Resultado {
  const [llegada, setLlegada] = useState<Aviso | null>(null);
  const [dentroDeRadio, setDentroDeRadio] = useState<Set<number>>(new Set());
  const [distancias, setDistancias] = useState<Map<number, number>>(new Map());
  const [candidatos, setCandidatos] = useState<PuntoPublico[]>([]);

  const registrarVisita = useRegistrarVisita();
  /** Puntos con peticion en vuelo o ya confirmados en esta sesion. */
  const enCurso = useRef<Set<number>>(new Set());
  const confirmados = useRef<Set<number>>(new Set());
  /**
   * Empates que la persona ya resolvio, por su combinacion de puntos. Sin esto
   * se volveria a preguntar en cada lectura del GPS mientras siga en la zona,
   * que es cada pocos segundos.
   */
  const empatesResueltos = useRef<Set<string>>(new Set());

  const descartarLlegada = useCallback(() => {
    setLlegada(null);
  }, []);

  const registrar = useCallback(
    (punto: PuntoPublico, lat: number, lon: number, precision: number) => {
      enCurso.current.add(punto.id);
      registrarVisita.mutate(
        { punto_id: punto.id, lat, lon, precision_metros: precision },
        {
          onSuccess: (respuesta) => {
            confirmados.current.add(punto.id);
            enCurso.current.delete(punto.id);
            // Solo la primera visita, y solo segun el servidor, dispara el aviso.
            if (respuesta.primera_visita) {
              setLlegada({
                punto,
                primeraVez: true,
                preguntaDisponible: respuesta.pregunta_disponible,
              });
            }
          },
          onError: () => {
            // Se libera el candado para poder reintentar en la siguiente
            // lectura: puede haber sido un corte de cobertura momentaneo.
            enCurso.current.delete(punto.id);
          },
        },
      );
    },
    [registrarVisita],
  );

  const elegirPunto = useCallback(
    (punto: PuntoPublico) => {
      setCandidatos((actuales) => {
        if (actuales.length > 1) {
          empatesResueltos.current.add(claveDeEmpate(actuales));
        }
        return [];
      });
      if (posicion) registrar(punto, posicion.lat, posicion.lon, posicion.precision);
    },
    [posicion, registrar],
  );

  useEffect(() => {
    if (!posicion || puntos.length === 0) return;

    const excluidos = new Set([...enCurso.current, ...confirmados.current]);
    const clasificacion = clasificarLlegada(
      puntos,
      { lat: posicion.lat, lon: posicion.lon },
      excluidos,
    );

    setDistancias(clasificacion.distancias);
    setDentroDeRadio(new Set(clasificacion.dentro.map((p) => p.id)));

    if (!activo) return;
    // Lectura demasiado imprecisa: sirve para pintar distancias aproximadas,
    // pero no para dar por bueno que se ha llegado.
    if (posicion.precision > PRECISION_MAXIMA_ACEPTABLE) return;

    if (clasificacion.candidatos.length > 1) {
      const clave = claveDeEmpate(clasificacion.candidatos);
      if (empatesResueltos.current.has(clave)) return;
      setCandidatos(clasificacion.candidatos);
      return;
    }

    setCandidatos([]);
    if (clasificacion.elegido) {
      registrar(clasificacion.elegido, posicion.lat, posicion.lon, posicion.precision);
    }
  }, [posicion, puntos, activo, registrar]);

  return { llegada, descartarLlegada, dentroDeRadio, distancias, candidatos, elegirPunto };
}

/** Identifica un empate por los puntos que lo forman, sin importar el orden. */
function claveDeEmpate(puntos: readonly PuntoPublico[]): string {
  return puntos
    .map((p) => p.id)
    .sort((a, b) => a - b)
    .join('-');
}
